import React, { useCallback, useEffect, useRef, useState } from "react";
import { t } from "utils/i18n";
import { Fa } from "composants/Icon";
import Modal from "composants/Modal/Modal";
import toaster from "composants/toaster/toaster";
import { analyzeFileWithMistral, isAnalyzableDocumentFile } from "api/documentAnalysis";

function hasFileDrag(event: React.DragEvent): boolean {
  return Array.from(event.dataTransfer.types).includes("Files");
}

function getFirstDroppedFile(dataTransfer: DataTransfer): File | null {
  if (dataTransfer.files?.length) {
    return dataTransfer.files[0];
  }
  const item = Array.from(dataTransfer.items || []).find(entry => entry.kind === "file");
  return item?.getAsFile() ?? null;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function inlineMarkdown(text: string): string {
  return text
    .split(/(\*\*[^*]+\*\*)/g)
    .map(part => {
      const bold = part.match(/^\*\*(.+)\*\*$/);
      if (bold) {
        return `<strong>${escapeHtml(bold[1])}</strong>`;
      }
      return escapeHtml(part);
    })
    .join("");
}

type HtmlOptions = {
  headingTag?: "p" | "h2";
  headingClass?: string;
  listClass?: string;
  itemClass?: string;
  paragraphClass?: string;
};

function markdownToHtml(markdown: string, options: HtmlOptions = {}): string {
  if (!markdown) return "";

  const headingTag = options.headingTag ?? "p";
  const wrapHeading = (title: string) => {
    const content =
      headingTag === "p"
        ? `<strong>${inlineMarkdown(title)}</strong>`
        : inlineMarkdown(title);
    const classAttr = options.headingClass ? ` class="${options.headingClass}"` : "";
    return `<${headingTag}${classAttr}>${content}</${headingTag}>`;
  };

  const lines = markdown.replace(/\r\n/g, "\n").trim().split("\n");
  const blocks: string[] = [];
  let listItems: string[] = [];

  const flushList = () => {
    if (!listItems.length) return;
    const listClass = options.listClass ? ` class="${options.listClass}"` : "";
    blocks.push(`<ul${listClass}>${listItems.join("")}</ul>`);
    listItems = [];
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      continue;
    }

    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      flushList();
      blocks.push(wrapHeading(heading[1]));
      continue;
    }

    const bullet = line.match(/^[-*•]\s+(.*)$/);
    if (bullet) {
      const itemClass = options.itemClass ? ` class="${options.itemClass}"` : "";
      listItems.push(`<li${itemClass}>${inlineMarkdown(bullet[1])}</li>`);
      continue;
    }

    flushList();
    const paragraphClass = options.paragraphClass ? ` class="${options.paragraphClass}"` : "";
    blocks.push(`<p${paragraphClass}>${inlineMarkdown(line)}</p>`);
  }

  flushList();
  return blocks.join("");
}

function markdownToPreviewHtml(markdown: string): string {
  return `<div class="leading-snug text-sm">${markdownToHtml(markdown, {
    headingTag: "h2",
    headingClass: "text-lg font-bold my-2",
    listClass: "my-1 mb-2",
    itemClass: "ml-4 py-0.5",
    paragraphClass: "my-1"
  })}</div>`;
}

function markdownToEditorHtml(markdown: string): string {
  return markdownToHtml(markdown);
}

function buildInsertHtml(markdown: string, name: string | null): string {
  const body = markdownToEditorHtml(markdown);
  if (!name) return body;
  return `<p><strong>${escapeHtml(name)}</strong></p>${body}`;
}

export interface AiPdfSummaryModalProps {
  initialFile?: File | null;
  onAccept(html: string): void;
  onClose(): void;
}

const AiPdfSummaryModal: React.FC<AiPdfSummaryModalProps> = ({
  initialFile,
  onAccept,
  onClose
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const dragCountRef = useRef(0);
  const [fileName, setFileName] = useState<string | null>(null);
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyzeFile = useCallback(async (file: File) => {
    if (!isAnalyzableDocumentFile(file)) {
      const message = t("commun_ia_format_document", {
        defaultValue: "Seuls les fichiers PDF et Word (.docx, .doc) sont acceptés"
      });
      setError(message);
      toaster.error(message);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setResult("");
      setFileName(file.name);
      const generated = await analyzeFileWithMistral(file, "concise");
      setResult(generated.analysis);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t("commun_ia_erreur", {
              defaultValue: "Une erreur est survenue lors de l'assistance IA."
            });
      setError(message);
      toaster.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDroppedFile = useCallback(
    (file: File | null) => {
      if (!file) return;
      analyzeFile(file);
    },
    [analyzeFile]
  );

  useEffect(() => {
    if (initialFile) {
      analyzeFile(initialFile);
    }
  }, [initialFile, analyzeFile]);

  const canInsert = !!result && !loading && !error;

  return (
    <Modal
      show
      title={t("commun_ia_synthese_document", { defaultValue: "Synthèse PDF / Word" })}
      onClose={() => onClose()}
      onValidate={() => {
        if (canInsert) {
          onAccept(buildInsertHtml(result, fileName));
        } else {
          onClose();
        }
      }}
      hideValidate={!canInsert}
      validateComponent={
        canInsert ? (
          <button
            className="button is-success"
            onClick={() => onAccept(buildInsertHtml(result, fileName))}
          >
            {t("commun_ia_inserer_commentaire", { defaultValue: "Insérer dans le commentaire" })}
          </button>
        ) : undefined
      }
      disableFooterButton={loading}
      width="640px"
      minHeight="200px"
    >
      <div className="p-4 space-y-4">
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf,.docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
          hidden
          onChange={e => {
            handleDroppedFile(e.target.files?.[0] ?? null);
            e.target.value = "";
          }}
        />

        {!loading && (
          <div
            className="p-6 border-2 border-dashed rounded text-center"
            style={{
              borderColor: dragging ? "#3273dc" : "#dbdbdb",
              background: dragging ? "#f5f8ff" : "#fafafa",
              cursor: "pointer"
            }}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={e => {
              if (!hasFileDrag(e)) return;
              e.preventDefault();
              e.stopPropagation();
              dragCountRef.current += 1;
              setDragging(true);
            }}
            onDragOver={e => {
              if (!hasFileDrag(e)) return;
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = "copy";
              setDragging(true);
            }}
            onDragLeave={e => {
              if (!hasFileDrag(e)) return;
              e.preventDefault();
              e.stopPropagation();
              dragCountRef.current = Math.max(0, dragCountRef.current - 1);
              if (dragCountRef.current === 0) {
                setDragging(false);
              }
            }}
            onDrop={e => {
              e.preventDefault();
              e.stopPropagation();
              dragCountRef.current = 0;
              setDragging(false);
              handleDroppedFile(getFirstDroppedFile(e.dataTransfer));
            }}
          >
            <Fa icon="file-alt" size="2x" className="has-text-link mb-3" />
            <p className="mb-1">
              {t("commun_ia_choisir_document", {
                defaultValue: "Déposez un PDF ou un Word (.docx) ou cliquez pour le sélectionner"
              })}
            </p>
            <p className="is-size-7 has-text-grey">
              {t("commun_ia_synthese_pdf_aide", {
                defaultValue: "Une synthèse courte sera insérée dans le commentaire."
              })}
            </p>
            {fileName && (
              <p className="is-size-7 mt-2">
                <Fa icon="file" className="mr-1" />
                {fileName}
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center p-8">
            <Fa icon="spinner" spin size="2x" className="text-primary mb-4" />
            <p className="text-gray-600">
              {t("commun_ia_synthese_en_cours", {
                defaultValue: "Synthèse du document en cours…"
              })}
            </p>
            <p className="is-size-7 has-text-grey mt-2">
              {t("commun_ia_synthese_pdf_scan", {
                defaultValue:
                  "Un PDF sans texte sélectionnable est lu automatiquement. Cela peut prendre un peu plus de temps."
              })}
            </p>
            {fileName && <p className="is-size-7 has-text-grey mt-2">{fileName}</p>}
          </div>
        ) : error ? (
          <div className="p-4 has-background-danger-light has-text-danger">
            <Fa icon="exclamation-triangle" className="mr-2" />
            {error}
          </div>
        ) : result ? (
          <div className="space-y-2">
            <p className="has-text-grey is-size-7">
              {t("commun_ia_apercu", { defaultValue: "Aperçu" })}
            </p>
            <div
              className="p-4 border rounded"
              style={{ maxHeight: "50vh", overflow: "auto", background: "#fff" }}
              dangerouslySetInnerHTML={{ __html: markdownToPreviewHtml(result) }}
            />
          </div>
        ) : null}
      </div>
    </Modal>
  );
};

export default AiPdfSummaryModal;
